import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { HomeOutlined, TruckOutlined } from '@ant-design/icons';

// COMPONENTS
import ToastErrorAlert from 'components/ToastAlert/ToastErrorAlert';

// DS
import {
  Col,
  VertTypography,
  VertIcon,
  VertSkeleton,
} from '@vertem-core/vertice-ds';

// SERVICES
import usePipeContext from 'Services/Providers/PipeProvider/usePipeContext';

// STYLES
import 'Assets/styles/default/components/Checkout/CheckoutFreightStyles.less';

// UTILS
import { formatDayMonthYear } from 'Utils/commons/date';
import { handleObjectAddress } from 'Utils/handleObjectAddress';
import { isObjectEmpty } from 'Utils';
import { pointsMask } from 'Utils/commons/points';
import { ShippingAddressFormat } from 'Shared/ShippingAddressFormat';
import generateUUID from 'Utils/generateUUID';
import ynToBool from 'Utils/ynToBool';

const initialState = {
  customer: {
    name: '',
    documentNumber: '',
    email: '',
    genderType: '',
    personType: '',
    birthDate: '',
  },
  shippingAddress: {
    zipCode: '',
    receiver: '',
    number: '',
    complement: '',
    reference: '',
    street: '',
    district: '',
    city: '',
    state: '',
  },
  isFilled: false,
};

// CHECKOUT FREIGHT COMPONENT
const CheckoutFreight = () => {
  /* Hooks */
  // @ts-ignore
  const { callApi } = usePipeContext();
  const Cart = useSelector(({ Cart: data }) => data);
  const Catalog = useSelector(({ Catalog: data }) => data);
  const Participants = useSelector(({ Participants: data }) => data);
  const ShippingCart = useSelector(({ Shipping: data }) => data);
  const { isVirtualVoucher } = useSelector(
    ({ CartReducerTable: data }) => data
  );

  /* States */
  const [Address, setCombinedAddress] = useState(initialState);

  /* Vars */
  const {
    currencyConfiguration: { decimalPlaces, pointsName },
  } = Catalog;
  const { value } = ShippingCart;
  const totalShipping = value?.total;
  const isShippingFree = totalShipping === 0;
  const isLoading = ShippingCart?.isLoading || Cart?.isLoading;
  const deliveryDates = value.vendors.map(
    (vendor) => new Date(vendor.deliveryDate)
  );
  const dateInit = new Date(Math.min(...deliveryDates));
  const dateEnd = new Date(Math.max(...deliveryDates));

  const formattedTotalShipping = pointsMask(
    totalShipping,
    decimalPlaces,
    pointsName
  );

  /* Utils */
  const hasError = () => {
    if (ShippingCart?.error && !isObjectEmpty(ShippingCart?.error)) {
      return true;
    }
    if (Cart?.error && !isObjectEmpty(Cart?.error)) {
      return true;
    }
    return false;
  };

  const compareDate = (dateInit, dateEnd) => {
    if (dateInit === dateEnd) {
      return `${formatDayMonthYear(dateInit)}`;
    }
    return `${formatDayMonthYear(dateInit)} à ${formatDayMonthYear(dateEnd)}`;
  };

  /* Lifecycles */
  useEffect(() => {
    if (Cart?.cartFinishRequests && Participants?.finishRequests) {
      const result = handleObjectAddress(Cart, Participants);
      const CombinedAddress = ShippingAddressFormat(result, Participants);
      if (!hasError()) {
        setCombinedAddress({ ...CombinedAddress, isFilled: true });
      }
    }
  }, [Cart?.cartFinishRequests, Participants?.finishRequests]);

  useEffect(() => {
    if (Address.isFilled) {
      const aliasUUID = generateUUID();
  
      callApi({
        symbol: 'UPDATE_CART_SHIPPING',
        alias: aliasUUID,
        payload: {
          ...Address,
          customer: {
            ...Address.customer,
            phones: Address.customer.phones.map((phone)=>{
                return {
                  ...phone,
                  number: phone.number.replace(/[^0-9]/g, '')
                }
            }) || [],

          }
        },


      });
    }
  }, [Address]);

  /* Renders */

  if(ynToBool(process.env.REACT_APP_HIDE_FREIGHT)) return null;

  if (hasError()) {
    return (
      <Col className="checkout-freight-wrapper col-12 col-md-auto">
        <Col className="checkout-freight-title">
          <VertIcon component={HomeOutlined} />
          <VertTypography.Title>Frete</VertTypography.Title>
        </Col>
        <Col className="col-10 col-md-7 ">
          <ToastErrorAlert
            error={ShippingCart?.error || Cart?.error}
            className={`margin-bottom-small`}
          />
        </Col>
      </Col>
    );
  }
  return (
    <Col className="checkout-freight-wrapper col-12 col-md-auto">
      <Col className="checkout-freight-title">
        <VertIcon component={TruckOutlined} />
        <VertTypography.Title>Frete</VertTypography.Title>
      </Col>
      {isLoading ? (
        <VertSkeleton active loading={isLoading} paragraph={{ rows: 1 }} />
      ) : (
        <Col className="col-10 col-md-7 ">
          <VertTypography.Paragraph className='checkout-freight-value'>
            Valor: {isShippingFree && <span className='free'>Grátis</span>}
            {!isShippingFree && (
              <span>
                {formattedTotalShipping.value} {formattedTotalShipping.label}
              </span>
            )}
          </VertTypography.Paragraph>

          {!isVirtualVoucher && (
            <VertTypography.Paragraph
              aria-label={`Previsão de entrega: ${compareDate(
                dateInit.toISOString(),
                dateEnd.toISOString()
              )}`}
            >
              Previsão de entrega:{' '}
              <span>
                {compareDate(dateInit.toISOString(), dateEnd.toISOString())}
              </span>
            </VertTypography.Paragraph>
          )}
        </Col>
      )}
    </Col>
  );
};

export default CheckoutFreight;
